import { Download, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BasicMarkdown, PlainText } from '@nucleus-commerce/admin-kit/thread';
import type { StaffChatMessage } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { conversationsApi } from '../api/conversations-api';
import { formatBytes } from '../lib/format-bytes';

import { JewelleryCard } from './jewellery-card';

export interface PeopleIndex {
  /** Staff user id → name, from the chat agents, participants, presence and the session. */
  name: (userId: unknown) => string;
}

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

/** A system row: assignment, transfer, close, rating... in one short line. */
export function EventLine({ message, people }: { message: StaffChatMessage; people: PeopleIndex }) {
  const { t } = useTranslation('conversations');
  const payload = message.payload;
  const event = asString(payload.event) ?? 'unknown';
  const by = payload.byUserId ? people.name(payload.byUserId) : null;
  switch (event) {
    case 'assigned':
      return payload.rule === 'round_robin'
        ? t('events.assignedRoundRobin', { to: people.name(payload.toUserId) })
        : t('events.assigned', {
            to: people.name(payload.toUserId),
            by: by ?? t('events.someone'),
          });
    case 'transferred':
      return t('events.transferred', {
        from: people.name(payload.fromUserId),
        to: people.name(payload.toUserId),
        by: by ?? t('events.someone'),
      });
    case 'unassigned':
      return t('events.unassigned', { by: by ?? t('events.someone') });
    case 'joined':
      return t('events.joined', { name: people.name(payload.userId) });
    case 'left':
      return t('events.left', { name: people.name(payload.userId) });
    case 'department_changed':
      return t('events.departmentChanged', { by: by ?? t('events.someone') });
    case 'tagged': {
      const added = Array.isArray(payload.added) ? payload.added.join(', ') : '';
      const removed = Array.isArray(payload.removed) ? payload.removed.join(', ') : '';
      return [
        added ? t('events.tagsAdded', { tags: added }) : null,
        removed ? t('events.tagsRemoved', { tags: removed }) : null,
      ]
        .filter(Boolean)
        .join(' · ');
    }
    case 'closed':
      return t(`events.closedBy.${asString(payload.by) ?? 'staff'}`, {
        defaultValue: t('events.closedBy.staff'),
      });
    case 'reopened':
      return t(`events.reopenedBy.${asString(payload.by) ?? 'staff'}`, {
        defaultValue: t('events.reopenedBy.staff'),
      });
    case 'rated':
      return t('events.rated', {
        rating: t(
          `rating.${payload.rating === 1 ? 'good' : payload.rating === -1 ? 'bad' : 'neutral'}`,
        ),
      });
    case 'missed':
      return t('events.missed');
    case 'converted':
      return t('events.converted', { by: by ?? t('events.someone') });
    case 'sla_breached': {
      const targets = Array.isArray(payload.targets) ? payload.targets : [];
      return targets.includes('first_response')
        ? t('events.slaBreachedFirstResponse')
        : t('events.slaBreachedResolution');
    }
    case 'request_opened': {
      const policy = payload.policy as { text?: unknown } | null | undefined;
      return [
        t('events.requestOpened', {
          kind: t(`jewellery.requestKind.${asString(payload.requestKind) ?? 'other'}`, {
            defaultValue: t('jewellery.requestKind.other'),
          }),
          order: asString(payload.orderName) ?? '',
        }),
        asString(policy?.text),
      ]
        .filter(Boolean)
        .join(' · ');
    }
    case 'draft_order_created':
      return t('events.draftOrderCreated', {
        name: asString(payload.name) ?? '',
        by: by ?? t('events.someone'),
      });
    default:
      return t('events.other', { event });
  }
}

function Attachments({ message, storeId }: { message: StaffChatMessage; storeId: string }) {
  const { t } = useTranslation('conversations');
  if (message.attachments.length === 0) return null;
  const open = async (uploadId: string, filename: string) => {
    const blob = await conversationsApi.attachment(storeId, message.conversationId, uploadId);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };
  return (
    <ul className="mt-1 space-y-1">
      {message.attachments.map((file) => (
        <li key={file.uploadId}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-auto max-w-full justify-start gap-2 py-1 text-left"
            onClick={() => void open(file.uploadId, file.filename)}
            aria-label={t('message.download', { name: file.filename })}
          >
            <FileText className="size-4 shrink-0" aria-hidden />
            <span className="truncate">{file.filename}</span>
            <span className="text-muted-foreground shrink-0 text-xs">
              {formatBytes(file.bytes)}
            </span>
            <Download className="size-3.5 shrink-0" aria-hidden />
          </Button>
        </li>
      ))}
    </ul>
  );
}

function FormAnswers({ answers }: { answers: unknown }) {
  if (!answers || typeof answers !== 'object') return null;
  // Custom design answers are labelled rows; pre-chat forms are a key → value record.
  if (Array.isArray(answers)) {
    const rows = answers.filter(
      (row): row is { key: string; label: string; value: string } =>
        typeof (row as { label?: unknown }).label === 'string' &&
        typeof (row as { value?: unknown }).value === 'string',
    );
    return (
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        {rows.map((row) => (
          <div key={row.key} className="contents">
            <dt className="opacity-70">{row.label}</dt>
            <dd className="break-words">{row.value}</dd>
          </div>
        ))}
      </dl>
    );
  }
  const entries = Object.entries(answers as Record<string, unknown>).filter(
    ([, value]) => value !== null && value !== '',
  );
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
      {entries.map(([key, value]) => (
        <div key={key} className="contents">
          <dt className="opacity-70">{key}</dt>
          <dd className="break-words">
            {typeof value === 'string' ? value : JSON.stringify(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** One message as staff see it: text, card, files. Staff markdown is a safe subset; shopper text is plain. */
export function MessageBody({ message, storeId }: { message: StaffChatMessage; storeId: string }) {
  const { t } = useTranslation('conversations');
  const text = message.body ? (
    message.authorType === 'visitor' ? (
      <PlainText text={message.body} />
    ) : (
      <BasicMarkdown text={message.body} />
    )
  ) : null;
  const payload = message.payload;
  let card = null;
  if (message.kind === 'form') {
    card = (
      <div className="space-y-1">
        <p className="text-xs font-medium">
          {payload.preset === 'custom_design'
            ? t('jewellery.customDesign')
            : t('message.formAnswers')}
        </p>
        <FormAnswers answers={payload.answers} />
      </div>
    );
  } else if (
    message.kind === 'product' ||
    message.kind === 'order_status' ||
    message.kind === 'discount' ||
    message.kind === 'invoice' ||
    message.kind === 'size_guide'
  ) {
    card = <JewelleryCard message={message} storeId={storeId} />;
  }
  return (
    <div className="space-y-1">
      {text}
      {card}
      <Attachments message={message} storeId={storeId} />
    </div>
  );
}
